<script>
	import { onMount, onDestroy } from "svelte";
	import "../app.css";
	import { page } from "$app/stores";
	import { browser } from "$app/environment";

	import { initialized } from "$lib/utilities/fbInit";
	import { store } from "$lib/utilities/store";
	import { theme } from "$lib/utilities/theme";
	import { translate } from "$lib/utilities/translate";
	import {
		getChangedListenerAliases,
		getListenerCriteriaSignature,
	} from "$lib/utilities/listenerCriteria.js";

	import { redirectToLangRoute } from "$lib/utilities/redirectToLangRoute";
	import { getFunctions, httpsCallable } from "firebase/functions";

	import Header from "$lib/sections/Header.svelte";
	import Menus from "$lib/sections/Menus.svelte";
	import Banner from "$lib/sections/Banner.svelte";
	import Alert from "$lib/components/Alert.svelte";
	import FloatingMenu from "$lib/sections/FloatingMenu.svelte";
	import Footer from "$lib/sections/Footer.svelte";

	// let { children, data } = $props();
	export let data;

	const appVersion = __APP_VERSION__;
	$: buildVersion =
		data?.content?.settings?.["App Settings"]?.buildVersion?.value;

	let lang;
	$: lang = $page.data.lang;
	$: pathParts = $page.url.pathname.split("/").filter(Boolean);
	$: isEmbed = ["embed", "embed-live"].includes(pathParts.at(-3));

	let area;
	$: area = $page.data.area;

	const addRtl = (dir) => {
		if (browser) {
			if (dir === "rtl") {
				document.body.classList.add("rtl");
			} else {
				document.body.classList.remove("rtl");
			}
		}
	};
	$: dir = data?.currentLanguageOption?.dir;
	$: addRtl(dir);

	// Build a deterministic fingerprint for listener preconditions so we only
	// recreate costly listeners when the underlying data changes.
	const normalizeObject = (value) => {
		if (value === undefined) return null;
		if (value instanceof Date) return value.toISOString();

		if (Array.isArray(value)) {
			return value.map((item) => normalizeObject(item));
		}

		if (value && typeof value === "object") {
			return Object.keys(value)
				.sort()
				.reduce((acc, key) => {
					acc[key] = normalizeObject(value[key]);
					return acc;
				}, {});
		}

		return value;
	};

	const getRolesSignature = (roles = {}) =>
		Object.entries(roles)
			.filter(([, enabled]) => Boolean(enabled))
			.map(([role]) => role)
			.sort()
			.join("|");

	const getCriteriaSignature = (criteria = {}) => {
		const criteriaToSign = JSON.parse(JSON.stringify(criteria));
		for (const col of Object.keys(criteriaToSign)) {
			if (!criteriaToSign[col].limit) {
				const keysToTrack = ["sortFields", "limit", "filters"];
				for (const key of Object.keys(criteriaToSign[col])) {
					if (!keysToTrack.includes(key))
						delete criteriaToSign[col][key];
				}
			}
		}
		const signature = JSON.stringify(
			Object.entries(criteriaToSign)
				.sort(([a], [b]) => a.localeCompare(b))
				.reduce((acc, [alias, value]) => {
					acc[alias] = normalizeObject(value);
					return acc;
				}, {}),
		);
		return signature;
	};

	const getListenerSignature = (profile, roles) => {
		if (!browser || !profile?.uid || !roles) return null;
		return `${profile.uid}|${getRolesSignature(roles)}|${getCriteriaSignature(
			profile.collectionCriteria,
		)}`;
	};

	let activeListenerSignature = null;
	let activeListenerAliases = [];
	let activeListenerCriteriaSignatures = {};
	let activeListenerRolesSignature = null;
	let authenticatedUser = null;
	let ensuringInitialAdmin = false;
	let profileUpdate;

	const ensureInitialAdmin = async () => {
		if (
			!authenticatedUser ||
			!$store?.appSettingsDoc ||
			$store.appSettingsDoc.initialAdminInitialized?.value === "true" ||
			ensuringInitialAdmin
		)
			return;

		ensuringInitialAdmin = true;
		try {
			await httpsCallable(getFunctions(), "ensureInitialAdmin")();
		} catch (error) {
			console.error("Unable to ensure initial admin role", error);
		} finally {
			ensuringInitialAdmin = false;
		}
	};

	const handleAuthStateChanged = async (user) => {
		authenticatedUser = user;
		await profileUpdate(user);
		await ensureInitialAdmin();
	};
	const ensureCollectionListeners = async (profile, roles) => {
		const signature = getListenerSignature(profile, roles);

		if (!signature) {
			activeListenerSignature = null;
			activeListenerRolesSignature = null;
			return;
		}

		const rolesSignature = getRolesSignature(roles);
		const mod = await import("$lib/utilities/createListeners.js");
		const aliases = mod.getListenerAliases();
		const criteriaSignatures = Object.fromEntries(
			aliases.map((alias) => [
				alias,
				getListenerCriteriaSignature(profile.collectionCriteria?.[alias]),
			]),
		);
		const membershipChanged =
			aliases.length !== activeListenerAliases.length ||
			aliases.some((alias) => !activeListenerAliases.includes(alias));
		if (signature !== activeListenerSignature) {
			const changedAliases = getChangedListenerAliases(
				aliases,
				activeListenerCriteriaSignatures,
				criteriaSignatures,
			);
			if (!activeListenerSignature || membershipChanged || rolesSignature !== activeListenerRolesSignature) {
				await mod.createListeners();
			} else if (changedAliases.length) {
				await mod.createListeners(undefined, changedAliases);
			}
		}

		activeListenerSignature = signature;
		activeListenerAliases = aliases;
		activeListenerCriteriaSignatures = criteriaSignatures;
		activeListenerRolesSignature = rolesSignature;
	};

	const initialize = async (init) => {
		if (!init) return;

		console.log("setup settings doc listener for all clients");

		const mod = await import("$lib/utilities/createDocumentListener.js");
		await mod.createDocumentListener(
			"appSettingsDoc",
			"settings/App Settings",
		);

		console.log("setup auth state listener");
		const { getAuth } = await import("firebase/auth");
		const auth = getAuth();

		const pMod = await import("$lib/utilities/profileUpdate.js");
		profileUpdate = pMod.profileUpdate;
		auth.onAuthStateChanged(handleAuthStateChanged);

		console.log("check if href is email signin link");
		const eMod = await import("$lib/utilities/loginForm.js");
		const checkEmailLogin = eMod.checkEmailLogin;
		await checkEmailLogin();
	};
	$: initialize($initialized);
	$: appSettingsDoc = $store?.appSettingsDoc;
	$: if (authenticatedUser && appSettingsDoc) void ensureInitialAdmin();

	$: ensureCollectionListeners($store.userProfile, $store.userRoles);

	// $: util.watchObject(util, "util");
	// $: util.watchObject({ ...$store, set: store.set }, "store");

	let GetAppSettingsComponent = null;
	let getAppSettingsLoading = false;

	const loadGetAppSettings = async () => {
		if (GetAppSettingsComponent || getAppSettingsLoading || !browser)
			return;
		getAppSettingsLoading = true;
		const mod = await import("$lib/components/GetAppSettings.svelte");
		GetAppSettingsComponent = mod.default;
		getAppSettingsLoading = false;
	};

	$: if ($store.userRoles?.admin) {
		void loadGetAppSettings();
	}

	onMount(() => {
		redirectToLangRoute(data);
	});

	onDestroy(() => {
		console.log("root +layout onDestroy");
		typeof $store?.[`appSettingsDoc`] === "function" &&
			$store?.[`appSettingsDoc`]();
		$store?.[`userProfileListener`] && $store?.[`userProfileListener`]();
		$store?.[`userRolesListener`] && $store?.[`userRolesListener`]();

		const collections = $store.userProfile?.collectionCriteria || {};

		for (const alias of Object.keys(collections)) {
			console.log(`removing ${alias} listener`);
			$store?.[`${alias}Listener`] && $store?.[`${alias}Listener`]();
		}
	});

	const compareVersions = (
		settingsVersion,
		settingsBuild,
		appVersion,
		buildVersion,
	) => {
		// If settingsVersion is undefined or null, ignore the check
		if (!settingsVersion) return false;

		const settingsParts = [
			...settingsVersion.split(".").map(Number),
			Number(settingsBuild),
		];
		const appParts = [
			...appVersion.split(".").map(Number),
			Number(buildVersion),
		];

		for (let i = 0; i < settingsParts.length; i++) {
			if (settingsParts[i] > appParts[i]) return true; // Settings version is higher, prompt reload
			if (settingsParts[i] < appParts[i]) return false; // App version is higher, ignore
		}

		return false; // Versions are the same, ignore
	};

	$: docAppVersion = $store?.appSettingsDoc?.appVersion?.value;
	$: docBuildVersion = $store?.appSettingsDoc?.appBuild?.value;

	$: updateAvailable = compareVersions(
		docAppVersion,
		docBuildVersion,
		appVersion,
		buildVersion,
	);

	$: showReload(updateAvailable);
	const showReload = (ua) => {
		if (!browser) return;
		if (ua) {
			const elem = document.getElementById("request-app-reload");
			elem && elem.classList.remove("hidden");
		}
	};

	$: appVersionUpdateMessage = `${
		translate(
			$store?.appSettingsDoc?.labels?.value?.appUpdateMessage,
			lang,
		) ||
		"A new version of the app is available. Please reload to get the latest features."
	} (v${appVersion} b${buildVersion} => v${$store?.appSettingsDoc?.appVersion?.value} b${$store?.appSettingsDoc?.buildVersion?.value})`;
</script>

<svelte:head>
	<style>
		html:has(main.embed-page),
		body:has(main.embed-page) {
			background: transparent !important;
		}
	</style>
</svelte:head>

<main
	class:embed-page={isEmbed}
	dir={data?.currentLanguageOption?.dir}
	class="fixed inset-0 max-h-screen overflow-hidden"
>
	{#if isEmbed}
		<Alert
			elemId="request-app-reload"
			message={appVersionUpdateMessage}
			type="info"
			useHtml={false}
		/>
		<div
			class="scroll-smooth overflow-y-scroll max-h-screen relative"
			id="main-scrollable-container"
		>
			<slot />
		</div>
	{:else}
		<div class="drawer h-full">
			<label for="mobile-menu-drawer" class="sr-only">
				Toggle mobile navigation menu
			</label>
			<input
				id="mobile-menu-drawer"
				type="checkbox"
				class="drawer-toggle"
			/>
			<div
				class="drawer-content flex flex-col overflow-y-auto scroll-smooth overscroll-contain touch-pan-y h-full"
			>
				<Alert
					elemId="request-app-reload"
					message={appVersionUpdateMessage}
					type="info"
					useHtml={false}
				/>

				{#if lang}
					<Header {data} {lang} {area} />
				{/if}

				<div
					class="scroll-smooth overflow-y-scroll max-h-screen relative"
					id="main-scrollable-container"
				>
					<slot />
					{#if lang}
						<Footer {data} {lang} {area} />
					{/if}
				</div>

				{#if lang}
					<FloatingMenu {data} {lang} />
				{/if}

				{#if $store.userRoles?.admin && GetAppSettingsComponent}
					<svelte:component this={GetAppSettingsComponent} />
				{/if}
			</div>
			<div class="drawer-side z-30">
				<label
					for="mobile-menu-drawer"
					aria-label="close sidebar"
					class="drawer-overlay"
				></label>
				<ul
					data-theme={$theme}
					class="menu w-80 p-0 overflow-y-auto scroll-smooth max-h-screen block"
				>
					<li class="sticky top-0 z-40 bg-base-100">
						<label
							for="mobile-menu-drawer"
							aria-label="close sidebar"
							class="flex justify-between hover:cursor-pointer"
						>
							<Banner {data} {lang} />
							<div>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="24px"
									height="24px"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
									class="feather feather-arrow-left"
									><line x1="19" y1="12" x2="5" y2="12"
									></line><polyline points="12 19 5 12 12 5"
									></polyline>
								</svg>
							</div>
						</label>
					</li>
					{#if lang}
						<ul class="block">
							<Menus {data} {lang} {area} />
						</ul>
					{/if}
				</ul>
			</div>
		</div>
	{/if}
</main>
